import { useEffect, useRef, useState } from "react";

export function useIncomingRingtone(ringing: boolean) {
  const context = useRef<AudioContext | null>(null);
  const [blocked, setBlocked] = useState(false);
  const enable = () => {
    try {
      context.current ??= new AudioContext();
    } catch {
      setBlocked(true);
      return;
    }
    setBlocked(context.current.state !== "running");
    void context.current
      .resume()
      .then(() => setBlocked(context.current?.state !== "running"))
      .catch(() => setBlocked(true));
  };
  // A normal interaction with Disk unlocks sound before the next incoming call.
  useEffect(() => {
    const unlock = () => {
      if (!context.current || context.current.state === "suspended") enable();
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      void context.current?.close();
      context.current = null;
    };
  }, []);
  useEffect(() => {
    if (!ringing) return;
    try {
      enable();
    } catch {
      setBlocked(true);
      return;
    }
    const ctx = context.current;
    if (!ctx) return;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    const oscillators = [480, 620].map((frequency) => {
      const osc = ctx.createOscillator();
      osc.frequency.value = frequency;
      osc.connect(gain);
      osc.start();
      return osc;
    });
    const ring = () => {
      const now = ctx.currentTime;
      gain.gain.cancelScheduledValues(now);
      for (const offset of [0, 0.6]) {
        gain.gain.setValueAtTime(0, now + offset);
        gain.gain.linearRampToValueAtTime(0.12, now + offset + 0.02);
        gain.gain.setValueAtTime(0.12, now + offset + 0.35);
        gain.gain.linearRampToValueAtTime(0, now + offset + 0.4);
      }
    };
    ring();
    const timer = setInterval(ring, 3000);
    return () => {
      clearInterval(timer);
      oscillators.forEach((osc) => {
        osc.stop();
        osc.disconnect();
      });
      gain.disconnect();
    };
  }, [ringing]);
  return { blocked, enable };
}
